import React from 'react';
import { Button } from './ui/button';
import { HeartPulse, Clock, ArrowRight } from 'lucide-react';

const benefits = [
  'Učinkovito blaženje bolečin in trebušnih krčev.',
  'Pomoč pri napenjanju in zaprtju.',
  'Manj joka, več spanja.',
  'Zmanjšanje napetosti v telesu za boljše splošno počutje.',
  'Ob koncu prejmete gradivo, ki vam bo v varno pomoč pri izvajanju masaže doma.',
];

const TummyProgramSection = () => {
  const scrollToContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
  <section
    id="tummy-program"
    className="relative overflow-hidden scroll-mt-20"
    data-testid="tummy-program-section"
  >

    {/* =========================================
        HERO – PRVI EKRAN
        ========================================= */}
    <div className="relative min-h-[calc(100vh-72px)] flex items-center overflow-hidden">

      {/* Mamica levo */}
      <img
        src="/icons/vd_hero_trebuscek_mama.png"
        alt=""
        aria-hidden="true"
        className="
          hidden md:block
          absolute
          left-[-2%] lg:left-[1%]
          bottom-[40px]
          w-[30rem] lg:w-[39rem]
          opacity-[0.28]
          pointer-events-none
          select-none
          z-0
        "
        data-testid="tummy-decor"
      />

      {/* Nežen sij za mamico */}
      <div
        aria-hidden="true"
        className="
          hidden md:block
          absolute
          left-[-5%]
          top-[5%]
          w-[38rem] lg:w-[46rem]
          h-[38rem] lg:h-[46rem]
          rounded-full
          pointer-events-none
          z-0
        "
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(223,210,188,0.28) 0%, rgba(223,210,188,0.14) 48%, rgba(223,210,188,0.05) 68%, transparent 82%)',
          filter: 'blur(18px)',
        }}
      />

      {/* Hero besedilo */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:ml-[31%] lg:ml-[34%] text-center">

          <h3
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-beige-800 font-bold tracking-tight leading-[1.1] pt-2 mb-5"
            data-testid="tummy-eyebrow"
          >
            Program za trebušček
          </h3>

          <h2
            className="font-serif font-normal text-2xl sm:text-3xl text-stone-800 max-w-3xl mx-auto mb-6 leading-[1.35]"
            data-testid="tummy-title"
          >
            Ko dojenček joka zaradi trebuščka,
            <br />
            si vsaka mama želi samo eno
            <br />
            <span className="italic">- da bi znala pomagati.</span>
          </h2>

          <div
            className="inline-flex items-center gap-2 bg-beige-100 text-beige-800 px-4 py-2 rounded-full text-base font-bold mb-6"
            data-testid="tummy-duration"
          >
            <HeartPulse size={16} />
            <Clock size={16} />
            <span>Eno srečanje · 60 minut</span>
          </div>

          <p className="text-stone-600 leading-relaxed max-w-2xl mx-auto">
            Ta program je namenjen prav temu. Naučiš se posebnega IAIM protokola za pomoč pri kolikah, napenjanju in zaprtju. Na srečanju se ustavimo za hranjenje, uspavanje ali objeme.
          </p>

        </div>
      </div>

      {/* Valovi na dnu hero sekcije */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1440 130"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 w-full h-[105px] lg:h-[125px] pointer-events-none z-[1]"
      >
        <path
          d="M-40 48 C150 6 290 104 485 55 C680 7 820 111 1015 61 C1200 15 1335 93 1480 48"
          fill="none"
          stroke="rgba(133,119,94,0.25)"
          strokeWidth="1.15"
        />
        <path
          d="M-40 72 C150 32 310 127 500 79 C690 31 840 126 1025 82 C1205 39 1340 114 1480 76"
          fill="none"
          stroke="rgba(133,119,94,0.17)"
          strokeWidth="1"
        />
        <path
          d="M-40 94 C155 58 315 142 510 101 C700 60 850 143 1040 104 C1220 67 1350 132 1480 99"
          fill="none"
          stroke="rgba(133,119,94,0.11)"
          strokeWidth="0.9"
        />
      </svg>

    </div>


    {/* =========================================
        VSEBINA – VIDNA ŠELE PO SCROLLU
        ========================================= */}
    <div className="relative py-14 lg:py-20 overflow-hidden">

      {/* Nežna velika mamica v ozadju spodnjega dela strani */}
      <img
        src="/icons/vd_hero_trebuscek_mama.png"
        alt=""
        aria-hidden="true"
        className="
          hidden md:block
          absolute
          left-[-8%]
          top-[2%]
          w-[42rem] lg:w-[54rem]
          opacity-[0.055]
          pointer-events-none
          select-none
          z-0
        "
      />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

                <div className="relative mb-6 px-6 py-9 sm:px-9 lg:px-12 lg:py-11">

          {/* Nepravilen, ročno narisan okvir */}
          <svg
            aria-hidden="true"
            viewBox="0 0 1000 520"
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full pointer-events-none"
          >
            {/* Glavna svinčnik črta */}
            <path
              d="
                M42 29
                C145 18, 287 28, 421 23
                C574 17, 735 31, 944 25
                C970 24, 981 44, 976 79
                C969 169, 982 273, 971 390
                C967 448, 958 483, 924 489
                C764 497, 620 485, 470 492
                C315 500, 168 485, 57 492
                C28 493, 18 469, 23 429
                C29 329, 16 225, 25 115
                C28 70, 25 39, 42 29
                Z
              "
              fill="none"
              stroke="rgba(111,91,65,0.44)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Druga nežna črta za učinek svinčnika */}
            <path
              d="
                M47 34
                C160 26, 300 32, 432 28
                C590 22, 742 35, 937 30
                C964 29, 974 49, 970 82
                C965 178, 978 279, 966 386
                C962 442, 953 476, 919 483
                C765 490, 615 481, 466 487
                C310 494, 174 480, 62 487
                C35 488, 26 464, 30 427
                C35 324, 22 224, 31 119
                C34 77, 31 45, 47 34
                Z
              "
              fill="none"
              stroke="rgba(111,91,65,0.16)"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* Vsebina */}
          <div className="relative z-10">

            <p className="text-stone-700 text-[15px] leading-relaxed mb-7 text-center italic">
              Prijeme prikazujem na lutki, ti pa jih nežno izvajaš na svojem otroku. Domov boš odšla pomirjena, z občutkom, da veš, kako pomagati.
            </p>

            <h4
              className="font-serif text-2xl text-stone-800 font-bold mb-5 text-center"
              data-testid="tummy-benefits-heading"
            >
              Kaj prinaša redna masaža?
            </h4>

            <ul className="space-y-4">
              {benefits.map((text, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-beige-50 transition-colors"
                  data-testid={`tummy-benefit-${i}`}
                >
                  <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 bg-beige-700 rounded-full" />
                  <p className="text-stone-700 text-[15px] leading-relaxed">
                    {text}
                  </p>
                </li>
              ))}
            </ul>

          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={scrollToContact}
            className="bg-beige-700 hover:bg-beige-800 text-white rounded-full px-8 py-6 text-base font-semibold transition-all hover:shadow-xl hover:-translate-y-0.5"
            data-testid="tummy-cta"
          >
            Rezerviraj srečanje za trebušček
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>

      </div>
    </div>

    </section>
  );
};

export default TummyProgramSection;